<script>
export default {
  name: "ExcursionsAndEvents",
};
</script>

<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  reactive,
  unref,
  watch,
} from "vue";
import { useVModel } from "@vueuse/core";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { cloneDeep, sortBy, isEqual } from "lodash";
import PlusIcon from "@/components/shared/icons/PlusIcon.vue";
import ExpandingSection from "@/components/shared/section/ExpandingSection.vue";
import BasicSwitch from "@/components/shared/input/BasicSwitch.vue";
import ExcursionsSelectionModal from "@/components/program-manager/sessions/components/ExcursionsAndEvents/ExcursionsSelectionModal.vue";
import FromLibrary from "@/components/program-manager/sessions/components/ExcursionsAndEvents/FromLibrary.vue";
import Configurator from "@/components/program-manager/sessions/components/ExcursionsAndEvents/Configurator.vue";
import ErrorMessage from "@/components/shared/ErrorMessage.vue";
import { useGetOccurrences } from "@/components/program-manager/sessions/composable";
import { columnsConfig } from "@/components/program-manager/sessions/components/ExcursionsAndEvents/constants";
import { transformOccurrences } from "@/components/program-manager/sessions/components/ExcursionsAndEvents/utils";
const props = defineProps({
  modelValue: {
    type: Object,
  },
  isSessionOwner: {
    type: Boolean
  }
});

const emit = defineEmits(["update:modelValue"]);
const programSession = useVModel(props, "modelValue", emit);

const validateSessionEventSelectionRules = (rule) => {
  let stack = [rule];
  while (stack.length > 0) {
    const value = stack.pop();
    if (value?.value) {
      if (value?.value?.libraries?.length > 0) {
        for (const library of value.value.libraries) {
          if (!library?.value?.id) {
            return false;
          }
        }
      }
      if (value?.value?.rules?.length > 0) {
        value?.value?.rules.forEach((val) => stack.push(val));
      }
    }
  }
  return true;
};

const rules = {
  session_event_selection_rules: {
    defaultIDRequired: helpers.withMessage(
      "Please make sure all dropdowns have an EXCURSION/EVENT selected.",
      validateSessionEventSelectionRules
    ),
  },
};

const v$ = useVuelidate(rules, programSession, {
  $registerAs: "ExcursionsAndEvents",
  $lazy: true,
});

const state = reactive({
  modalIsOpen: false,
  noDesignatedHousing: false,
  selectedExcursions: [],
  canExtendStay: !!props.modelValue.extend_hotel_max,
  noDesignatedExcursionsEvents: false,
  initialLoading: props.modelValue?.session_events?.length > 0,
});

const occurrenceIds = computed(
  () =>
    unref(programSession)
      ?.session_events?.filter((item) => item?.event_id)
      ?.map((item) => item?.event_id) || []
);

const {
  execute: executeFetchOccurrencesOptions,
  isLoading: ocurrencesLoading,
  state: ocurrencesOptions,
} = useGetOccurrences(
  { immediate: occurrenceIds.value?.length > 0, throwError: false },
  {
    occurrence_ids: occurrenceIds.value,
    limit: occurrenceIds.value?.length ?? 20,
    ...(programSession.value?.session_travel_details?.[0]?.arrival_date &&
    occurrenceIds.value?.length
      ? {
          pricing_date:
            programSession.value?.session_travel_details?.[0]?.arrival_date,
        }
      : undefined),
  }
);

const addExcursionOrEvent = () => {
  state.modalIsOpen = true;
};

const updateExcursions = (val) => {
  state.modalIsOpen = false;
  unref(programSession).convertedSessionEvents = sortBy(
    cloneDeep(val),
    "title"
  );
};

const removeOccurrence = (id) => {
  const index = unref(programSession).convertedSessionEvents.findIndex(
    (val) => val?.id === id
  );
  if (index >= 0) {
    unref(programSession).convertedSessionEvents.splice(index, 1);
  }
};

const updateRules = (node, events) => {
  if (node?.libraries?.length > 0) {
    node.libraries.forEach(function (entry, index, theArray) {
      const event = events.find((val) => val?.id === entry?.value?.id);
      theArray[index].value = { ...event, ...entry.value };
    });
    if (node?.rules?.length > 0) {
      node.rules.forEach((val) => updateRules(val.value, events));
    }
  }
};

watch(
  () => unref(programSession)?.session_events,
  (sessionEvents, oldSessionEvents) => {
    const ids = sessionEvents?.filter((item) => item?.event_id)
      ?.map((item) => item?.event_id) || [];
    const oldIds = oldSessionEvents?.filter((item) => item?.event_id)
      ?.map((item) => item?.event_id) || [];
    if (ids.length > 0 && !isEqual(ids, oldIds)) {
      state.initialLoading = true;
      executeFetchOccurrencesOptions(0, {
        occurrence_ids: ids,
        limit: ids?.length ?? 20,
        cancel: true,
      });
    }
  },
  { immediate: false, deep: true }
);

watch(
  [() => ocurrencesOptions, () => props.modelValue.session_events],
  ([occurrences, sessionEvents]) => {
    const transformedOccurrences = transformOccurrences(
      unref(occurrences)?.data
    );
    if (transformedOccurrences?.length > 0) {
      const eventFoundMap =
        sessionEvents?.reduce((acc, val) => {
          if (val?.event_id) {
            acc[val.event_id] = true;
          }
          return acc;
        }, {}) || [];
      const foundEvents = transformedOccurrences?.filter(
        (val) => eventFoundMap[val?.id]
      );
      unref(programSession).convertedSessionEvents = foundEvents;
      updateRules(
        unref(programSession).session_event_selection_rules.value,
        transformedOccurrences
      );
    }
    state.initialLoading = false;
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <ExpandingSection field-label="Excursions and Events" class="col-span-2">
    <template #content>
      <div class="grid grid-cols-1 gap-x-24 gap-y-4">
        <BasicSwitch
          field-label="No designated excursions and events"
          @update:modelValue="state.noDesignatedExcursionsEvents"
        />
        <ExcursionsSelectionModal
          :terms="props.modelValue.terms"
          :modal-is-open="state.modalIsOpen"
          :selected-occurrences="programSession.convertedSessionEvents"
          :session_travel_details="props.modelValue.session_travel_details"
          :columns-config="columnsConfig"
          @update-selected-occurrences="updateExcursions"
          @close-modal="state.modalIsOpen = false"
        />
        <div class="flex justify-between items-center">
          <div class="common-label-text">Excursions or Events</div>
          <div
            v-if="!state.noDesignatedExcursionsEvents"
            data-testid="add-excursions-modal-open-button"
            class="cursor-pointer"
            @click="addExcursionOrEvent"
          >
            <PlusIcon class="mr-2 text-teal-900 w-8 h-8" />
          </div>
        </div>
        <FromLibrary
          v-if="
            state.initialLoading ||
            programSession.convertedSessionEvents?.length > 0
          "
          :is-session-owner="props.isSessionOwner"
          :selected-occurrences="programSession.convertedSessionEvents"
          :events-updating="state.initialLoading || ocurrencesLoading"
          @remove-occurrence="removeOccurrence"
        />
        <Configurator
          v-if="
            programSession.convertedSessionEvents?.length > 0 &&
            !state.initialLoading
          "
          v-show="!state.initialLoading && !ocurrencesLoading"
          v-model="programSession.session_event_selection_rules.value"
          class="py-4"
          :select-options="programSession.convertedSessionEvents"
          :show-label="true"
        />
        <ErrorMessage
          error-classes="common-error-text"
          :vuelidate-instance="v$.session_event_selection_rules"
        />
      </div>
    </template>
  </ExpandingSection>
</template>
